import { useState, useEffect, useCallback } from "react";
import { X, Search, FileText, Loader2, Paperclip, Check } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (v) => new Intl.NumberFormat("fr-FR").format(Math.round(v || 0));

const TARGET_CATEGORIES = [
  { value: "facture", label: "Facture récapitulative" },
  { value: "depot", label: "Facture cachetée (Dépôt)" },
  { value: "cheque", label: "Chèque" },
  { value: "remise", label: "Remise de chèque / Bordereau" },
  { value: "bon", label: "Bon" },
  { value: "ordonnance", label: "Ordonnance" },
  { value: "general", label: "Pièce justificative" },
];

/**
 * Modal pour rattacher UN ou plusieurs documents archivés à un dossier facture.
 * Flux inverse de ArchiveImportModal :
 *   - Archives → choisir LE dossier facture cible (ce composant)
 *   - Dossier facture → choisir LES archives à attacher (ArchiveImportModal)
 *
 * Props :
 *   - documentIds: string[]    (IDs des documents archivés à rattacher)
 *   - defaultCategory: string  (catégorie pré-sélectionnée, ex: "facture")
 *   - onClose: () => void
 *   - onAttached: (invoice) => void   (callback après succès)
 */
export default function AttachToInvoiceModal({ documentIds, defaultCategory = "facture", onClose, onAttached }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [targetCategory, setTargetCategory] = useState(defaultCategory);
  const [attaching, setAttaching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/log/invoices`, { headers: getAuthHeaders() });
      const all = res.data.invoices || res.data || [];
      setInvoices(Array.isArray(all) ? all : []);
    } catch (e) {
      toast.error("Erreur chargement des dossiers factures");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = invoices.filter((inv) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.insurer_name?.toLowerCase().includes(q) ||
      inv.patient_name?.toLowerCase().includes(q)
    );
  }).slice(0, 50);

  const handleAttach = async () => {
    if (!selectedInvoice) {
      toast.error("Sélectionnez un dossier facture");
      return;
    }
    if (!documentIds || documentIds.length === 0) {
      toast.error("Aucun document à rattacher");
      return;
    }
    setAttaching(true);
    try {
      const res = await axios.post(
        `${API}/log/invoices/${selectedInvoice.id}/photos/from-archive`,
        { document_ids: documentIds, category: targetCategory },
        { headers: getAuthHeaders() }
      );
      const attachedCount = res.data.attached?.length || 0;
      const skippedCount = res.data.skipped?.length || 0;
      if (attachedCount > 0) {
        toast.success(
          `${attachedCount} document(s) rattaché(s) au dossier ${selectedInvoice.invoice_number}` +
          (skippedCount > 0 ? ` (${skippedCount} déjà présents)` : "")
        );
      } else if (skippedCount > 0) {
        toast.warning(`Document(s) déjà présent(s) sur le dossier ${selectedInvoice.invoice_number}`);
      }
      if (onAttached) onAttached(selectedInvoice);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du rattachement");
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="attach-to-invoice-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-emerald-400" />
              Rattacher à un dossier facture
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {documentIds?.length || 0} document(s) à rattacher · Choisissez le dossier cible et la catégorie
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" data-testid="attach-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-white/5 grid grid-cols-1 md:grid-cols-3 gap-2 shrink-0">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par n° facture, assureur, patient..."
              className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none text-sm"
              data-testid="attach-modal-search"
            />
          </div>
          <select
            value={targetCategory}
            onChange={(e) => setTargetCategory(e.target.value)}
            className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm"
            data-testid="attach-modal-target-category"
          >
            {TARGET_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>Rattacher en : {c.label}</option>
            ))}
          </select>
        </div>

        {/* Invoice list */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-slate-500 text-center py-10 text-sm">
              {search ? "Aucun dossier facture correspondant" : "Aucun dossier facture disponible"}
            </p>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((inv) => {
                const isSelected = selectedInvoice?.id === inv.id;
                return (
                  <button
                    key={inv.id}
                    onClick={() => setSelectedInvoice(inv)}
                    data-testid={`attach-target-invoice-${inv.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border transition-all text-left ${
                      isSelected
                        ? "bg-emerald-500/10 border-emerald-500/40"
                        : "bg-slate-950/50 border-white/5 hover:border-white/20 hover:bg-white/[0.02]"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "bg-emerald-500/20" : "bg-slate-800"
                    }`}>
                      {isSelected ? <Check className="w-4 h-4 text-emerald-400" /> : <FileText className="w-4 h-4 text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-white">{inv.invoice_number}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                          {inv.insurer_name || "—"}
                        </span>
                        {inv.patient_name && (
                          <span className="text-[10px] text-slate-500">{inv.patient_name}</span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">
                          {inv.doc_type || "facture"}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Montant <span className="text-slate-300 font-medium">{fmt(inv.amount)} FCFA</span>
                        {inv.photos?.length > 0 && (
                          <span className="ml-2">· {inv.photos.length} doc(s) déjà attaché(s)</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between shrink-0">
          <p className="text-xs text-slate-500">
            {selectedInvoice
              ? <>Cible : <span className="text-emerald-400 font-medium">{selectedInvoice.invoice_number}</span></>
              : "Aucune cible sélectionnée"}
          </p>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline" className="border-white/10 text-slate-400 hover:bg-white/5">
              Annuler
            </Button>
            <Button
              onClick={handleAttach}
              disabled={!selectedInvoice || attaching || !documentIds?.length}
              className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
              data-testid="attach-modal-confirm"
            >
              {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              Rattacher au dossier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
